import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Loader2,
  Check,
  X,
  Key,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRegistrationStore } from '@/stores/registration-store';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';

import type { RegistrationBatch, RegistrationItem, RecentBatch } from '@/stores/registration-store';

function getStatusIcon(status: RegistrationItem['status']): React.ReactElement {
  switch (status) {
    case 'registering':
      return <Loader2 className="h-3 w-3 animate-spin text-primary" />;
    case 'complete':
      return <Check className="h-3 w-3 text-success" />;
    case 'failed':
      return <X className="h-3 w-3 text-error" />;
    case 'env_leak':
      return <Key className="h-3 w-3 text-warning" />;
    case 'pending':
      return <Loader2 className="h-3 w-3 text-text-tertiary" />;
  }
}

function getBasename(path: string): string {
  const segments = path.replace(/\/$/, '').split('/');
  return segments[segments.length - 1] ?? path;
}

type AnyBatch = RegistrationBatch | RecentBatch;

function getSummaryText(batch: AnyBatch): { text: string; className: string } {
  const { completed, failed, total } = batch;
  const inProgress = completed + failed < total;

  if (inProgress) {
    return {
      text: `Adding ${completed + failed}/${total} projects...`,
      className: 'text-text-secondary',
    };
  }

  if (failed > 0 && completed > 0) {
    return {
      text: `${String(completed)} added, ${String(failed)} failed`,
      className: 'text-text-secondary',
    };
  }

  if (failed > 0 && completed === 0) {
    return {
      text: `${String(failed)} failed`,
      className: 'text-error',
    };
  }

  return {
    text: `${String(completed)} projects added`,
    className: 'text-success',
  };
}

function isBatchActive(batch: AnyBatch): boolean {
  return batch.completed + batch.failed < batch.total;
}

function QueueProgressSection({ batch }: { batch: RegistrationBatch }): React.ReactElement {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-elevated">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
        <span>Queue Progress</span>
        <Badge variant="secondary" className="ml-auto h-4 px-1.5 text-[10px]">
          {batch.total}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-0.5 px-3 pb-2">
          {batch.items.map(item => (
            <div key={item.path} className="flex items-center gap-2 py-0.5">
              {getStatusIcon(item.status)}
              <span className="font-mono text-xs text-text-primary">{getBasename(item.path)}</span>
              <span className="truncate font-mono text-xs text-text-tertiary">{item.path}</span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ErrorsSection({ items }: { items: RegistrationItem[] }): React.ReactElement {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-elevated">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
        <AlertCircle className="h-3 w-3 text-error" />
        <span>Errors</span>
        <Badge variant="destructive" className="ml-auto h-4 px-1.5 text-[10px]">
          {items.length}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-0.5 px-3 pb-2">
          {items.map(item => (
            <div key={item.path} className="flex flex-col gap-0.5 py-0.5">
              <span className="font-mono text-xs text-text-primary">{getBasename(item.path)}</span>
              {item.error != null && <span className="text-xs text-error">{item.error}</span>}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function VaultMigrationsSection({ items }: { items: RegistrationItem[] }): React.ReactElement {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-elevated">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
        <ShieldCheck className="h-3 w-3 text-primary" />
        <span>Vault Migrations</span>
        <Badge variant="secondary" className="ml-auto h-4 px-1.5 text-[10px]">
          {items.length}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-0.5 px-3 pb-2">
          {items.map(item => (
            <div key={item.path} className="flex flex-col gap-0.5 py-0.5">
              <span className="font-mono text-xs text-text-primary">{getBasename(item.path)}</span>
              {item.migratedKeys != null && item.migratedKeys.length > 0 && (
                <span className="text-xs text-text-tertiary">
                  {item.migratedKeys.map((k: { file: string; key: string }) => k.key).join(', ')}
                </span>
              )}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function InfoPanel(): React.ReactElement {
  const activeBatch = useRegistrationStore(state => state.activeBatch);
  const recentBatches = useRegistrationStore(state => state.recentBatches);

  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [completeFaded, setCompleteFaded] = useState(false);

  const userToggledRef = useRef(false);
  const autoCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevActiveBatchIdRef = useRef<string | null>(null);

  const displayBatch = activeBatch ?? (recentBatches.length > 0 ? recentBatches[0] : null);
  const isActive = activeBatch !== null && isBatchActive(activeBatch);

  // Auto-show when a batch starts
  useEffect(() => {
    if (activeBatch !== null && activeBatch.batchId !== prevActiveBatchIdRef.current) {
      prevActiveBatchIdRef.current = activeBatch.batchId;
      setVisible(true);
      setCompleteFaded(false);
      userToggledRef.current = false;

      // Auto-expand when batch starts
      setExpanded(true);

      // Clear any pending timers
      if (autoCollapseTimerRef.current !== null) {
        clearTimeout(autoCollapseTimerRef.current);
        autoCollapseTimerRef.current = null;
      }
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    }
  }, [activeBatch]);

  // Auto-collapse 5s after batch completes (if user hasn't manually toggled)
  useEffect((): (() => void) => {
    if (activeBatch !== null && !isBatchActive(activeBatch) && !userToggledRef.current) {
      autoCollapseTimerRef.current = setTimeout(() => {
        setExpanded(false);
        autoCollapseTimerRef.current = null;
      }, 5000);
    }

    return (): void => {
      if (autoCollapseTimerRef.current !== null) {
        clearTimeout(autoCollapseTimerRef.current);
        autoCollapseTimerRef.current = null;
      }
    };
  }, [activeBatch]);

  // Fade complete summary after 10s when no active batch
  useEffect((): (() => void) => {
    if (
      activeBatch === null &&
      displayBatch !== null &&
      !isBatchActive(displayBatch) &&
      displayBatch.failed === 0
    ) {
      fadeTimerRef.current = setTimeout(() => {
        setCompleteFaded(true);
        fadeTimerRef.current = null;
      }, 10000);
    }

    return (): void => {
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [activeBatch, displayBatch]);

  // Hide when no batch and faded
  useEffect((): (() => void) | undefined => {
    if (displayBatch === null || completeFaded) {
      const timer = setTimeout(() => {
        setVisible(false);
        setExpanded(false);
      }, 300);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [displayBatch, completeFaded]);

  const handleToggle = useCallback(() => {
    userToggledRef.current = true;
    setExpanded(prev => !prev);
  }, []);

  const batchItems = activeBatch?.items ?? [];
  const failedItems = batchItems.filter((item: RegistrationItem) => item.status === 'failed');
  const migratedItems = batchItems.filter(
    (item: RegistrationItem) => item.migratedKeys != null && item.migratedKeys.length > 0
  );

  const shouldShow = visible && displayBatch !== null && !completeFaded;

  return (
    <div
      className={cn(
        'overflow-hidden border-border bg-surface transition-all duration-300 ease-in-out',
        shouldShow ? 'border-t' : 'h-0'
      )}
    >
      {/* Collapsed bar */}
      <div className="flex h-8 items-center justify-between px-3 text-xs">
        <div className="flex items-center gap-2">
          {isActive && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
          )}
          {!isActive && displayBatch !== null && displayBatch.failed > 0 && (
            <span className="inline-flex h-2 w-2 rounded-full bg-error" />
          )}
          {!isActive && displayBatch !== null && displayBatch.failed === 0 && (
            <span className="inline-flex h-2 w-2 rounded-full bg-success" />
          )}
          {displayBatch !== null && (
            <span className={getSummaryText(displayBatch).className}>
              {getSummaryText(displayBatch).text}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleToggle}
          className="rounded p-0.5 text-text-tertiary transition-colors hover:bg-surface-elevated hover:text-text-secondary"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
      </div>

      {/* Expanded content */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          expanded ? 'max-h-64' : 'max-h-0'
        )}
      >
        {activeBatch !== null && (
          <ScrollArea className="max-h-56">
            <div className="space-y-1 pb-2">
              <QueueProgressSection batch={activeBatch} />
              {failedItems.length > 0 && <ErrorsSection items={failedItems} />}
              {migratedItems.length > 0 && <VaultMigrationsSection items={migratedItems} />}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
